// ServiceLad's "cheapest HPI check" landing page. Shared by the React page and
// the prerendered HTML so the two renderings say the same thing.
// ServiceLad only: the other brands answer 404 (see hasCheapestHpiPage).

import { packagesFor } from "../config/pricing.js";

export const CHEAPEST_HPI_PATH = "/cheapest-hpi-check";

// The day the competitor prices below were read from each provider's own site.
// Re-check them before changing this date or repeating the "cheapest" claim.
export const pricesCheckedOn = "2026-10-22";
export const pricesCheckedLabel = "22 October 2026";

// One full single check per provider, the cheapest that covers finance,
// stolen and write-off, as each provider's own pricing page showed it.
// Car Check (carcheck.co.uk) is left out: it sells the checks as add-ons and
// does not say its damage check covers write-offs.
export const competitorPrices = [
  { provider: "Experian AutoCheck", product: "Single vehicle check", price: "7.99" },
  { provider: "MyCarCheck", product: "Comprehensive check", price: "8.49" },
  { provider: "Total Car Check", product: "Gold Check", price: "9.99" },
  { provider: "CarAnalytics", product: "Vehicle History Check", price: "10.99" },
  { provider: "The Auto Experts", product: "Full history check", price: "10.99" },
  { provider: "VehicleScore", product: "Hidden History, one car", price: "13.95" },
  { provider: "The AA", product: "Comprehensive vehicle history check", price: "14.99" },
  { provider: "RAC", product: "Full Check", price: "14.99" },
  { provider: "HPI Check", product: "Full HPI check", price: "19.99" },
  { provider: "carVertical", product: "Check one car", price: "39.99" },
];

export function hasCheapestHpiPage(brandKey) {
  return brandKey === "servicelad";
}

export function cheapestHpiCopy() {
  const [risk, complete] = packagesFor("servicelad");
  return {
    risk,
    complete,
    metaTitle: `Cheapest HPI Check UK: Full Check £${risk.plate} | ServiceLad`,
    metaDescription: `The cheapest full HPI check we know of in the UK: £${risk.plate} for stolen, write-off, outstanding finance and mileage checks. Prices compared, checked ${pricesCheckedLabel}.`,
    h1: "The cheapest full HPI check in the UK",
    lead: `A full HPI check from ServiceLad costs £${risk.plate}. It answers the questions that matter before you buy: is the car reported stolen, has it been written off, and is there still finance owed on it. One car, one payment, no subscription.`,
    formHeading: `Run a full HPI check for £${risk.plate}`,
    formCopy: "Enter the registration of the car you are thinking of buying.",
    formButton: "Check this car",
    compareHeading: "How do HPI check prices compare?",
    compareNote: `Prices for one full check, read from each provider's own website on ${pricesCheckedLabel}. Prices change, so check the provider before you buy.`,
    includedHeading: `What does the £${risk.plate} check include?`,
    includedNote: "It is a full check, not a cut-down free one. If a record is not available for a particular car, the report says so rather than showing a pass.",
    whyHeading: "Why is it cheaper?",
    whyBody: [
      "The checks run online against UK vehicle data, including DVLA and DVSA records, and the report is ready in your browser. There is no phone line, membership or bundle of extra checks built into the price.",
      "Optional extras, such as V5C logbook alerts, are never ticked for you. You pay for the check you chose and nothing else.",
    ],
    differHeading: "Is a cheaper HPI check as thorough?",
    differBody: [
      "\"HPI check\" has become the everyday name for any vehicle history check, after the company that made it popular. Most full checks answer the same core questions: stolen, written off, outstanding finance and mileage.",
      "Where providers differ is the extras: some bundle several checks, some include a data guarantee, and some add more history. ServiceLad does not offer a data guarantee. If that matters to you, compare it with what you are paying.",
    ],
    serviceHeading: "Want the service history too?",
    serviceBody: `For £${complete.plate}, the ${complete.name} adds the service record the manufacturer holds for the car: the dates, the mileage at each visit and the garage that did the work. If the manufacturer has no records for the car, you are not charged for that part.`,
    faqHeading: "Cheap HPI check questions",
    faqs: [
      {
        q: "What is the cheapest HPI check in the UK?",
        a: `ServiceLad's Full HPI Check is £${risk.plate}, the lowest price we know of for a full check covering stolen, write-off and outstanding finance. We compared it with other UK providers on ${pricesCheckedLabel}.`,
      },
      {
        q: `Is a £${risk.plate} HPI check a full check?`,
        a: "Yes. It checks stolen markers, insurance write-off category, outstanding finance, recorded mileage, export and scrap history, and gives a valuation where one is available.",
      },
      {
        q: "Does it show outstanding finance?",
        a: "Yes. The check looks for finance agreements still recorded against the car. A car with money owed on it can be taken back by the lender, even after you have paid the seller.",
      },
      {
        q: "How quickly do I get the report?",
        a: "Usually straight after payment, in your browser. We also email you a link to the report, which works for 90 days.",
      },
      {
        q: "Is there a subscription?",
        a: "No. It is a one-off payment for one registration. Nothing renews and there is nothing to cancel.",
      },
      {
        q: "Can I check a car for free first?",
        a: "Yes. Enter any registration on ServiceLad to see its tax status, MOT history and basic details for free, then decide whether you need the full check.",
      },
    ],
  };
}
